/**
 * @fileoverview Connect the backend job catalog to saved activation choices, recent runs, and explicitly selected manual execution. `jobDocs.js` owns explanatory copy; backend scheduler/business gates determine actual admission. Enabled configuration, queued work, and acknowledged effects remain distinct. Changing schedule intent cannot retract already-running provider work.
 */

import React, { useCallback, useEffect, useState, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { AppButton, AppTag, AppSpinner, AppModal, AppSelect, AppToggle } from '../shared';
import api from '../../api';
import ToastContext from '../../ToastContext';
import JOB_DOCS from './jobDocs';
import AppIcon from "../shared/AppIcon";

const EMPTY_TESTER_CONFIG = {
  environment: '',
  configured: false,
  selection_required: false,
  profiles: [],
  warnings: []
};

const DEFAULT_JOB_GROUP = {
  key: 'other',
  name: 'Other Jobs',
  description: 'Scheduled automation jobs that have not been assigned to a domain.',
  order: Number.MAX_SAFE_INTEGER
};

const GROUP_VISUALS = {
  intake: { icon: 'pi pi-sitemap', tone: 'teal' },
  matter_operations: { icon: 'pi pi-briefcase', tone: 'blue' },
  email_calendar: { icon: 'pi pi-envelope', tone: 'indigo' },
  finance_billing: { icon: 'pi pi-dollar', tone: 'green' },
  document_sources: { icon: 'pi pi-file', tone: 'slate' },
  system_health: { icon: 'pi pi-heart', tone: 'red' },
  other: { icon: 'pi pi-cog', tone: 'slate' }
};



function numericSortValue(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}



function getGroupVisual(groupKey) {
  return GROUP_VISUALS[groupKey] || GROUP_VISUALS.other;
}



function buildJobGroups(jobs) {
  const groups = new Map();

  (jobs || []).forEach((job, index) => {
    const groupKey = job.group_key || DEFAULT_JOB_GROUP.key;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: groupKey,
        name: job.group_name || DEFAULT_JOB_GROUP.name,
        description: job.group_description || DEFAULT_JOB_GROUP.description,
        order: numericSortValue(job.group_order, DEFAULT_JOB_GROUP.order),
        jobs: []
      });
    }

    groups.get(groupKey).jobs.push({
      ...job,
      groupSortOrder: numericSortValue(job.job_order, index + 1)
    });
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      jobs: group.jobs.sort((first, second) =>
        first.groupSortOrder - second.groupSortOrder ||
        String(first.name || '').localeCompare(String(second.name || '')))
    }))
    .sort((first, second) =>
      first.order - second.order ||
      String(first.name || '').localeCompare(String(second.name || '')));
}



function getGroupCounts(jobs) {
  return (jobs || []).reduce(

  (counts, job) => {
    counts.total += 1;

    if (job.activation_toggle_supported) {
      if (job.is_active) {
        counts.active += 1;
      } else {
        counts.paused += 1;
      }
    }

    if (job.schedule_type === 'manual_only') {
      counts.manualOnly += 1;
    }

    if (job.schedule_type === 'webhook') {
      counts.webhook += 1;
    }

    return counts;
  }, {
    total: 0,
    active: 0,
    paused: 0,
    manualOnly: 0,
    webhook: 0
  });
}



function getOverallJobCounts(jobs, groups) {
  const counts = getGroupCounts(jobs);
  return {
    ...counts,
    groups: (groups || []).length,
    manualOnly: counts.manualOnly
  };
}



function getRunSeverity(status) {
  if (status === 'success') {
    return 'success';
  }
  if (status === 'queued') {
    return 'info';
  }
  if (status === 'skipped') {
    return 'warning';
  }
  return 'danger';
}



function JobCard({
  job,
  runs,
  onManualRun,
  onShowInfo,
  testerSelectionRequired,
  testerOptions,
  selectedTesterId,
  onTesterChange,
  onToggleActive,
  togglePending
}) {
  const [expanded, setExpanded] = useState(false);
  const jobRuns = runs || [];
  const hasRuns = jobRuns.length > 0;
  const cardClasses = [
    'scheduled-job-card',
    expanded ? 'is-expanded' : '',
    job.activation_toggle_supported && !job.is_active ? 'scheduled-job-card--inactive' : '',
    job.schedule_type === 'manual_only' ? 'scheduled-job-card--manual-only' : '',
    job.schedule_type === 'webhook' ? 'scheduled-job-card--webhook' : ''
  ].filter(Boolean).join(' ');
  const toggleExpanded =

  () => setExpanded((prev) => !prev);
  const handleSummaryKeyDown =

  (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <div className={cardClasses}>
      <div className="scheduled-job-card__topline">
        <div
          className="scheduled-job-card__summary"
          onClick={toggleExpanded}
          onKeyDown={handleSummaryKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          style={{ userSelect: 'none' }}>

          <div className="scheduled-job-card__identity">
            <AppIcon
              icon={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
              className="scheduled-job-card__chevron" />
            <div className="scheduled-job-card__copy">
              <div className="scheduled-job-card__title">{job.name}</div>
              <div className="scheduled-job-card__description">{job.description}</div>
            </div>
          </div>
        </div>

        <div className="scheduled-job-card__meta">
          <AppTag value={job.schedule_display} severity="info" className="scheduled-job-card__tag" />
          {job.activation_toggle_supported &&
            <AppTag
              value={job.is_active ? 'active' : 'inactive'}
              severity={job.is_active ? 'success' : 'warning'}
              className="scheduled-job-card__tag" />}
          {hasRuns &&
            <AppTag
              value={jobRuns[0].status}
              severity={getRunSeverity(jobRuns[0].status)}
              className="scheduled-job-card__tag" />}
          {JOB_DOCS[job.id] &&
            <button
              type="button"
              className="scheduled-job-card__info-button"
              onClick={() => onShowInfo(job)}
              aria-label={`Info for ${job.name}`}>
              <AppIcon icon="pi pi-info-circle" />
            </button>}
        </div>
      </div>

      {(job.activation_toggle_supported || job.manual_trigger_supported) &&
        <div className="scheduled-job-card__controls" onClick={(event) => event.stopPropagation()}>
          {job.activation_toggle_supported &&
            <div className="scheduled-jobs-toggle-row">
              <div className="scheduled-jobs-toggle-copy">
                <div className="scheduled-jobs-toggle-label">
                  {job.is_active ? 'Schedule active' : 'Schedule paused'}
                </div>
              </div>
              <AppToggle
                checked={job.is_active}
                onChange={(event) => onToggleActive(job.id, event.value)}
                disabled={togglePending}
                ariaLabel={`Toggle ${job.name} scheduled execution`} />
            </div>}

          {job.manual_trigger_supported &&
            <div className="scheduled-jobs-run-controls">
              {testerSelectionRequired &&
                <div className="scheduled-jobs-run-target-picker">
                  <label
                    htmlFor={`scheduled-job-tester-${job.id}`}
                    className="scheduled-jobs-run-target-label">
                    Manual-run Slack recipient
                  </label>
                  <AppSelect
                    id={`scheduled-job-tester-${job.id}`}
                    value={selectedTesterId || ''}
                    options={testerOptions}
                    placeholder="Select tester"
                    className="scheduled-jobs-run-target-select"
                    onChange={(value) => onTesterChange(job.id, value)} />
                </div>}

              <AppButton
                label="Run"
                icon="pi pi-play"
                onClick={() => onManualRun(job.id)}
                disabled={(testerSelectionRequired && !selectedTesterId) || togglePending}
                className="scheduled-job-card__run-button"
                style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }} />
            </div>}
        </div>}

      {expanded &&
        <div className="scheduled-job-card__details">
          {job.activation_toggle_supported && !job.is_active &&
            <div className="scheduled-jobs-inactive-note">
              This job is inactive, so scheduled executions are skipped until it is turned back on.
            </div>}

          {!hasRuns ?
            <div className="scheduled-job-card__empty-history">No run history yet</div> :
            <div className="scheduled-job-card__history">
              {jobRuns.map((run) => {
                const meta = typeof run.metadata === 'string' ?
                  (() => { try { return JSON.parse(run.metadata); } catch { return {}; } })() :
                  run.metadata || {};
                const duration = meta.duration_seconds != null ?
                  `${meta.duration_seconds}s` :
                  null;

                return (
                  <div key={run.id} className="scheduled-job-card__history-row">
                    <AppTag
                      value={run.status}
                      severity={getRunSeverity(run.status)}
                      className="scheduled-job-card__history-status" />

                    <span className="scheduled-job-card__history-time app-data-value">
                      {run.created_at ?
                        new Date(run.created_at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        }) :
                        '-'}
                    </span>
                    {duration &&
                      <span className="scheduled-job-card__history-duration">
                        {duration}
                      </span>}
                    {run.message &&
                      <span className="scheduled-job-card__history-message">
                        {run.message}
                      </span>}
                  </div>);
              })}
            </div>}
        </div>}
    </div>);

}



function JobGroup({
  group,
  jobRuns,
  onManualRun,
  onShowInfo,
  testerSelectionRequired,
  testerOptions,
  selectedTesterByJob,
  onTesterChange,
  onToggleActive,
  savingActivationByJob
}) {
  const [expanded, setExpanded] = useState(true);
  const counts = getGroupCounts(group.jobs);
  const visual = getGroupVisual(group.key);
  const contentId = `scheduled-jobs-group-${group.key}`;

  return (
    <section className={`scheduled-jobs-group scheduled-jobs-group--${visual.tone}`} aria-labelledby={`${contentId}-title`}>
      <div className="scheduled-jobs-group__header">
        <button
          type="button"
          className="scheduled-jobs-group__toggle"
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.name}`}
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded((previous) => !previous)}>
          <span className="scheduled-jobs-group__icon" aria-hidden="true">
            <AppIcon icon={visual.icon} />
          </span>
          <span className="scheduled-jobs-group__copy">
            <span className="scheduled-jobs-group__title-row">
              <span id={`${contentId}-title`} className="scheduled-jobs-group__title">
                {group.name}
              </span>
              <AppIcon
                icon={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
                className="scheduled-jobs-group__chevron" />
            </span>
            <span className="scheduled-jobs-group__description">
              {group.description}
            </span>
          </span>
        </button>

        <div className="scheduled-jobs-group__stats" aria-label={`${group.name} summary`}>
          <AppTag value={`${counts.total} job${counts.total === 1 ? '' : 's'}`} severity="secondary" className="scheduled-jobs-group__tag" />
          {counts.active > 0 &&
            <AppTag value={`${counts.active} active`} severity="success" className="scheduled-jobs-group__tag" />}
          {counts.paused > 0 &&
            <AppTag value={`${counts.paused} paused`} severity="warning" className="scheduled-jobs-group__tag" />}
          {counts.manualOnly > 0 &&
            <AppTag value={`${counts.manualOnly} manual-only`} severity="secondary" className="scheduled-jobs-group__tag" />}
          {counts.webhook > 0 &&
            <AppTag value={`${counts.webhook} webhook`} severity="info" className="scheduled-jobs-group__tag" />}
        </div>
      </div>

      {expanded &&
        <div id={contentId} className="scheduled-jobs-group__jobs">
          {group.jobs.map((job) =>
          <JobCard
            key={job.id}
            job={job}
            runs={jobRuns[job.id]}
            onManualRun={onManualRun}
            onShowInfo={onShowInfo}
            testerSelectionRequired={testerSelectionRequired}
            testerOptions={testerOptions}
            selectedTesterId={selectedTesterByJob[job.id] || ''}
            onTesterChange={onTesterChange}
            onToggleActive={onToggleActive}
            togglePending={!!savingActivationByJob[job.id]} />

          )}
        </div>}
    </section>);

}



function ScheduledJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [jobRuns, setJobRuns] = useState({});
  const [infoJob, setInfoJob] = useState(null);
  const [manualSlackTesters, setManualSlackTesters] = useState(EMPTY_TESTER_CONFIG);
  const [selectedTesterByJob, setSelectedTesterByJob] = useState({});
  const [savingActivationByJob, setSavingActivationByJob] = useState({});
  const showToast = useContext(ToastContext);

  const fetchJobRuns = useCallback(

  async () => {
    try {
      const response = await api.get('/automations/jobs/runs');
      setJobRuns(response.data.runs || {});
    } catch (error) {
      console.error('Error fetching job runs:', error);
    }
  }, []);

  const fetchJobs = useCallback(

  async () => {
    setLoading(true);
    try {
      const response = await api.get('/automations/jobs');
      const nextJobs = response.data.jobs || [];
      const nextTesterConfig = response.data.manual_slack_testers || EMPTY_TESTER_CONFIG;
      setJobs(nextJobs);
      setManualSlackTesters(nextTesterConfig);
      setSelectedTesterByJob(

      (previous) => {
        const next = {};
        const defaultTesterId =
        nextTesterConfig.selection_required &&
        nextTesterConfig.profiles?.length === 1 ?

        nextTesterConfig.profiles[0].slack_user_id :
        '';
        nextJobs.forEach((job) => {
          next[job.id] = previous[job.id] || defaultTesterId;
        });
        return next;
      });
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching automation jobs:', error);
      showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load automation jobs',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const handleTesterChange = useCallback(

  (jobId, slackUserId) => {
    setSelectedTesterByJob((previous) => ({
      ...previous,
      [jobId]: slackUserId
    }));
  }, []);

  const handleManualRun = useCallback(

  async (jobId) => {
    if (!jobId) {
      return;
    }
    const selectedTesterId = selectedTesterByJob[jobId] || '';
    if (manualSlackTesters.selection_required && !selectedTesterId) {
      showToast({
        severity: 'warn',
        summary: 'Select Tester',
        detail: 'Choose a designated tester for this manual run first.',
        life: 3000
      });
      return;
    }
    try {
      const response = await api.post(`/automations/jobs/${jobId}/run`, selectedTesterId ?
      { tester_slack_user_id: selectedTesterId } :
      {});
      if (response.data?.success) {
        const queued = response.status === 202 || Boolean(response.data.task_id);
        showToast({
          severity: queued ? 'info' : 'success',
          summary: queued ? 'Queued' : 'Triggered',
          detail: response.data.message || 'Job triggered',
          life: 3000
        });
      } else {
        showToast({
          severity: 'error',
          summary: 'Error',
          detail: response.data?.error || 'Job trigger failed',
          life: 3000
        });
      }
      fetchJobs();
      fetchJobRuns();
    } catch (error) {
      console.error('Error running automation job:', error);
      const detail = error.response?.data?.error || 'Failed to trigger job';
      showToast({
        severity: 'error',
        summary: 'Error',
        detail,
        life: 3000
      });
    }
  }, [fetchJobs, fetchJobRuns, manualSlackTesters.selection_required, selectedTesterByJob, showToast]);

  const handleToggleActive = useCallback(

  async (jobId, isActive) => {
    if (!jobId) {
      return;
    }

    setSavingActivationByJob((previous) => ({
      ...previous,
      [jobId]: true
    }));

    try {
      const response = await api.patch(`/automations/jobs/${jobId}/active`, {
        is_active: isActive
      });
      const updatedJob = response.data?.job;

      setJobs((previous) => previous.map((job) =>
      job.id === jobId ?
      { ...job, ...(updatedJob || {}), is_active: isActive } :
      job
      ));

      showToast({
        severity: 'success',
        summary: 'Updated',
        detail: `${updatedJob?.name || 'Scheduled job'} ${isActive ? 'activated' : 'paused'}`,
        life: 3000
      });
    } catch (error) {
      console.error('Error updating scheduled job state:', error);
      const detail = error.response?.data?.error || 'Failed to update scheduled job state';
      showToast({
        severity: 'error',
        summary: 'Error',
        detail,
        life: 3000
      });
    } finally {
      setSavingActivationByJob((previous) => ({
        ...previous,
        [jobId]: false
      }));
    }
  }, [showToast]);

  const handleRefresh = useCallback(

  () => {
    fetchJobs();
    fetchJobRuns();
  }, [fetchJobs, fetchJobRuns]);

  useEffect(() => {
    fetchJobs();
    fetchJobRuns();
  }, [fetchJobs, fetchJobRuns]);

  // Poll run history so queued manual jobs update when the worker completes.
  useEffect(() => {
    const interval = setInterval(fetchJobRuns, 30000);
    return () => clearInterval(interval);
  }, [fetchJobRuns]);

  const renderTesterPanel =

  () => {
    if (!manualSlackTesters.configured) {
      return null;
    }

    const profiles = manualSlackTesters.profiles || [];
    const warnings = manualSlackTesters.warnings || [];
    const environment = String(manualSlackTesters.environment || '').trim().toLowerCase();
    const environmentLabel =
    environment === 'development' ? 'Development' :
    environment === 'staging' ? 'Staging' :
    'This environment';


    return (
      <section className="scheduled-jobs-tester-panel" aria-labelledby="scheduled-jobs-testers-title">
                <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div>
                        <h2 id="scheduled-jobs-testers-title" className="scheduled-jobs__section-title">Designated Slack Testers</h2>
                        <div className="text-sm text-600 mt-2">
                            {environmentLabel} manual runs redirect Slack delivery only to the selected tester for that job.
                        </div>
                    </div>
                    <AppTag
            value={profiles.length > 0 ? `${profiles.length} tester profile(s)` : 'No valid tester profiles'}
            severity={profiles.length > 0 ? 'warning' : 'danger'} />

                </div>

                {profiles.length > 0 &&
        <div className="scheduled-jobs-tester-grid">
                        {profiles.map((profile) =>
          <div key={profile.slack_user_id} className="scheduled-jobs-tester-card">
                                <div className="font-medium">{profile.name}</div>
                                <div className="text-sm text-600 mt-1">{profile.email}</div>
                                <div className="text-xs text-500 mt-2 app-data-value">
                                    {profile.slack_user_id}
                                </div>
                            </div>
          )}
                    </div>}


                {warnings.length > 0 &&
        <div className="scheduled-jobs-tester-warnings">
                        {warnings.map((warning) =>
          <div key={warning} className="scheduled-jobs-tester-warning">
                                <AppIcon icon="pi pi-exclamation-triangle" />
                                <span>{warning}</span>
                            </div>
          )}
                    </div>}

            </section>);

  };

  const testerOptions = (manualSlackTesters.profiles || []).map((profile) => ({
    label: profile.label,
    value: profile.slack_user_id
  }));
  const jobGroups = buildJobGroups(jobs);
  const jobCounts = getOverallJobCounts(jobs, jobGroups);

  return (
    <section className="scheduled-jobs" aria-labelledby="scheduled-jobs-title">
            {renderTesterPanel()}

            <header className="scheduled-jobs__overview">
                <div className="flex align-items-center justify-content-between">
                    <div>
                        <h2 id="scheduled-jobs-title" className="scheduled-jobs__title">Scheduled Jobs</h2>
                        <div className="text-sm text-600 mt-2">
                            {jobs.length} job{jobs.length === 1 ? '' : 's'} across {jobGroups.length} group{jobGroups.length === 1 ? '' : 's'}
                        </div>
                    </div>
                    <AppButton
            label="Refresh"
            icon="pi pi-refresh"
            onClick={handleRefresh} />

                </div>
                <div className="text-sm text-600 mt-2">
                    Last refreshed: {lastRefresh.toLocaleTimeString()}
                </div>
                <div className="scheduled-jobs-summary" aria-label="Scheduled jobs summary">
                    <div className="scheduled-jobs-summary__item">
                        <span className="scheduled-jobs-summary__value">{jobCounts.total}</span>
                        <span className="scheduled-jobs-summary__label">Total jobs</span>
                    </div>
                    <div className="scheduled-jobs-summary__item">
                        <span className="scheduled-jobs-summary__value">{jobCounts.groups}</span>
                        <span className="scheduled-jobs-summary__label">Groups</span>
                    </div>
                    <div className="scheduled-jobs-summary__item scheduled-jobs-summary__item--active">
                        <span className="scheduled-jobs-summary__value">{jobCounts.active}</span>
                        <span className="scheduled-jobs-summary__label">Active schedules</span>
                    </div>
                    <div className="scheduled-jobs-summary__item scheduled-jobs-summary__item--paused">
                        <span className="scheduled-jobs-summary__value">{jobCounts.paused}</span>
                        <span className="scheduled-jobs-summary__label">Paused schedules</span>
                    </div>
                    <div className="scheduled-jobs-summary__item">
                        <span className="scheduled-jobs-summary__value">{jobCounts.manualOnly}</span>
                        <span className="scheduled-jobs-summary__label">Manual-only</span>
                    </div>
                </div>
            </header>

            {loading && jobs.length === 0 ?
      <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <AppSpinner />
                </div> :

      jobGroups.map((group) =>
      <JobGroup
        key={group.key}
        group={group}
        jobRuns={jobRuns}
        onManualRun={handleManualRun}
        onShowInfo={setInfoJob}
        testerSelectionRequired={manualSlackTesters.selection_required}
        testerOptions={testerOptions}
        selectedTesterByJob={selectedTesterByJob}
        onTesterChange={handleTesterChange}
        onToggleActive={handleToggleActive}
        savingActivationByJob={savingActivationByJob} />

      )}


            <AppModal
        visible={!!infoJob}
        title={infoJob?.name || ''}
        onClose={() => setInfoJob(null)}
        width="640px"
        className="job-docs-modal">

                {infoJob && JOB_DOCS[infoJob.id] &&
        <div className="job-docs-content">
                        <ReactMarkdown>{JOB_DOCS[infoJob.id]}</ReactMarkdown>
                    </div>}

            </AppModal>

        </section>);

}

export default ScheduledJobs;
